/**
 * The v0.3 letter set (RFC §4) — letters are DATA; the mailbox system
 * evaluates triggers and applies effects. Copy is draft (v0.9 owns the final
 * pass); every letter obeys the satire rules: name the real behavior, fine
 * print carries the gag, offer letters print their requirements + price.
 */

import { EXPANSION, METERS, OFFICE, PRESTIGE } from "@/data/tunables";
import type { CareTierId } from "@/data/tunables";
import type { TierId } from "@/state/GameState";

export type Predicate =
  | { type: "tick"; atLeast: number }
  | { type: "queueCount"; atLeast: number }
  | { type: "firstIssue" }
  | { type: "anyDeathOrIssues"; issuesAtLeast: number }
  | { type: "patientsInTier"; tier: CareTierId; atLeast: number }
  | { type: "funding"; atLeast: number }
  | { type: "completionsAtFrontier"; atLeast: number; forExpansion?: boolean }
  | { type: "expansionEligible"; tier: Exclude<TierId, "detox"> }
  | { type: "frontierAtLeast"; tier: CareTierId }
  | { type: "deathsPending"; atLeast: number }
  | { type: "everyTicks"; period: number }
  | { type: "criminalActive" }
  | { type: "all"; of: Predicate[] };

export type Effect =
  | { type: "unlockTier"; tier: TierId }
  | { type: "makePurchasable"; tier: Exclude<TierId, "detox">; price: number }
  | { type: "capacityBonus"; tier: CareTierId; amount: number }
  | { type: "capacityMultiplier"; tiers: CareTierId[]; factor: number }
  | { type: "grantAutomatorLevel"; tier: CareTierId }
  | { type: "nameFacility" }
  | { type: "teach"; control: "recruit" | "admit" | "treat" }
  | { type: "offerPrestige" }
  | { type: "unlockBroker" }
  // v0.5 Death-Letter claw-back effects — scale with run.deathsPending:
  | { type: "spendFundingPerDeath"; perDeath: number } // fail-loud if unaffordable
  | { type: "clawbackRepPerDeath"; perDeath: number }
  | { type: "clawbackHeatPerDeath"; perDeath: number }
  | { type: "advanceCompliance" }
  | { type: "resolveDeaths" }
  // v0.6 criminal track:
  | { type: "unlockCriminal" }
  | { type: "deaSpike"; amount: number };

/** A branch of a choice-letter (the Death Letter). */
export interface LetterChoice {
  label: string;
  effects: Effect[];
}

export interface Letter {
  id: string;
  title: string;
  body: string[];
  finePrint?: string;
  trigger: Predicate;
  effects: Effect[];
  /** When present, the reader shows these branches instead of a single OPEN. */
  choices?: LetterChoice[];
  /** Repeatable letters are REMOVED on read so they can re-arrive (death, KPI). */
  repeatable?: boolean;
  autoTravel?: string; // SceneKey; validated at read time
}

function expansionOffer(tier: Exclude<TierId, "detox">, title: string, body: string[], gag: string): Letter {
  const cfg = EXPANSION[tier];
  return {
    id: `expansionOffer-${tier}`,
    title,
    body,
    finePrint: `${gag}  [Requirements met: ${cfg.requireCompletions} completions at your frontier · $${cfg.requireFunding.toLocaleString()} on hand. Expansion price: $${cfg.price.toLocaleString()}.]`,
    trigger: {
      type: "all",
      of: [
        { type: "expansionEligible", tier },
        { type: "completionsAtFrontier", atLeast: cfg.requireCompletions, forExpansion: true },
        { type: "funding", atLeast: cfg.requireFunding },
      ],
    },
    effects: [{ type: "makePurchasable", tier, price: cfg.price }],
  };
}

export const LETTERS: Letter[] = [
  {
    id: "welcome",
    title: "Welcome to the Recovery Industry",
    body: [
      "Congratulations on your decision to help people, professionally.",
      "Your operating license is enclosed. Please write your facility's name on the line provided. Choose something that sounds like a lakeside retreat.",
      "Begin by WORKING THE PHONES. Churches and charities are standing by, statistically.",
    ],
    finePrint: "*this letter does not constitute legal advice, medical advice, or advice",
    trigger: { type: "tick", atLeast: 2 },
    effects: [{ type: "nameFacility" }, { type: "teach", control: "recruit" }],
    autoTravel: "office",
  },
  {
    id: "accreditation",
    title: "RE: Your Accreditation — APPROVED",
    body: [
      "After a rigorous review (applicant: you; reviewer: you), your facility is hereby ACCREDITED for medically supervised detoxification.",
      "Your first bed is ready. ADMIT the front of your line and begin billing— begin healing.",
    ],
    finePrint: "*accreditation certificate suitable for framing; frame sold separately",
    trigger: { type: "queueCount", atLeast: 1 },
    effects: [{ type: "unlockTier", tier: "detox" }, { type: "teach", control: "admit" }],
    autoTravel: "tier-detox",
  },
  {
    id: "firstIssue",
    title: "RE: The Noises From Bed Two",
    body: [
      "Our clinical desk has reviewed your voicemail. What you are describing is called a MEDICAL ISSUE, and it is normal, expected, and billable.",
      "TREAT the patient before resuming care. Treated patients resume progressing; untreated ones remember.",
    ],
    finePrint: "*a clinical guide is available for purchase; this letter is not it",
    trigger: { type: "firstIssue" },
    effects: [{ type: "teach", control: "treat" }],
    autoTravel: "tier-detox",
  },
  expansionOffer(
    "residential",
    "Expansion Opportunity: Residential",
    [
      "Our records indicate your detox beds are producing completions. The next step in the continuum of care is a RESIDENTIAL program: same patients, longer stays, larger invoices.",
      "State approval is enclosed, contingent on payment of the approval fee.",
    ],
    "*approval was never in question",
  ),
  expansionOffer(
    "php",
    "Expansion Opportunity: Partial Hospitalization",
    [
      "PHP: all the billing codes of a hospital, almost none of the hospital.",
      "Your regional development officer has pre-approved this growth opportunity.",
    ],
    "*emphasis on partial",
  ),
  expansionOffer(
    "iop",
    "Expansion Opportunity: Intensive Outpatient",
    [
      "Patients live their lives; you bill their insurance. Everyone wins, defined loosely.",
    ],
    "*intensity may vary",
  ),
  expansionOffer(
    "soberLiving",
    "Expansion Opportunity: Sober Living",
    [
      "The halfway point deserves a whole house. Rent is due weekly, recovery is due whenever.",
    ],
    "*house rules apply; rules subject to house",
  ),
  expansionOffer(
    "alumni",
    "Your Alumni Network™ Charter",
    [
      "A network of people who no longer legally require you — but who might, statistically, again.",
      "Charter enclosed. Membership is free for them and priceless for you.",
    ],
    "*alumni outcomes tracked upon request; no requests received",
  ),
  {
    id: "inspection",
    title: "Inspection Report: PASSED",
    body: [
      "A state inspector visited your facility on a date. Everything observed was observed to be fine.",
      "Your bed cap has been raised in recognition of capacity you technically already had.",
    ],
    finePrint: "*inspector waved from car",
    trigger: { type: "patientsInTier", tier: "detox", atLeast: 3 },
    effects: [{ type: "capacityBonus", tier: "detox", amount: 1 }],
    autoTravel: "tier-detox",
  },
  {
    id: "incident",
    title: "RE: Recent Events",
    body: [
      "In light of recent events, we have hired someone.",
      "Your new Technician starts immediately and has been briefed on the location of the beds.",
    ],
    finePrint: "*briefing consisted of the location of the beds",
    trigger: { type: "anyDeathOrIssues", issuesAtLeast: 5 },
    effects: [{ type: "grantAutomatorLevel", tier: "detox" }],
    autoTravel: "tier-detox",
  },
  {
    id: "buildBasement",
    title: "Permit Approved: STORAGE ANNEX",
    body: [
      "Your permit application to convert the storage annex (stated usage: storage) has been approved.",
      "Three additional beds have been installed in the storage.",
    ],
    finePrint: "*storage of people",
    trigger: { type: "funding", atLeast: 2000 },
    effects: [{ type: "capacityBonus", tier: "detox", amount: 3 }],
    autoTravel: "tier-detox",
  },
  {
    id: "cbup",
    title: "CBUP License Enclosed",
    body: [
      "Congratulations: you now hold a Certificate of Beds, Unspecified Purpose.",
      "Under CBUP, every 2 beds may legally sleep 3, provided no one counts.",
    ],
    finePrint: "*counting available upon subpoena",
    trigger: { type: "completionsAtFrontier", atLeast: 10 },
    effects: [{ type: "capacityMultiplier", tiers: ["detox", "residential"], factor: 1.5 }],
    autoTravel: "tier-detox",
  },
  {
    id: "sellFacility",
    title: "An Exciting Partnership Opportunity",
    body: [
      "Our fund has been admiring your outcomes — specifically, your margins.",
      "We'd like to ACQUIRE your facility. You keep the reputation; we keep the building, the beds, and the billing relationships. You walk away with capital and a clean slate to do it all again.",
      "Open MORE → SELL THE FACILITY when you're ready to cash out. Nothing happens until you sign.",
    ],
    finePrint: "*your legacy of care is now a line item; line items are non-refundable",
    // Reachable on a fresh account (Detox frontier + completions); each sale
    // then advances the high-water mark so the next run opens one tier deeper.
    trigger: {
      type: "all",
      of: [
        { type: "frontierAtLeast", tier: PRESTIGE.offerMinFrontier },
        { type: "completionsAtFrontier", atLeast: PRESTIGE.offerMinCompletions },
      ],
    },
    effects: [{ type: "offerPrestige" }],
  },
  {
    id: "brokerInvitation",
    title: "A Business Proposition (re: your waiting list)",
    body: [
      "A man with a clipboard has noticed your intake line is often full. He represents interested parties.",
      "For a finder's fee, he'll take the overflow off your hands — the ones you can't bed anyway. Cash, same day, no paperwork.",
      "Once accepted, a BROKER option appears whenever your line is at capacity. It moves the person at the FRONT of the line. No questions, a little Heat.",
    ],
    finePrint: "*\"interested parties\" declined to be named; the van is unmarked for your privacy",
    trigger: { type: "queueCount", atLeast: OFFICE.queueCap },
    effects: [{ type: "unlockBroker" }],
    autoTravel: "office",
  },
  {
    id: "deathLetter",
    title: "Local Man Dies Days After Leaving Your Facility",
    body: [
      "A reporter is asking questions. There is a photograph. There is a grieving family and a number they keep repeating.",
      "You can let it run its course, spin it, or make it go away. The heat and the reputation are already on your books — the only question is how much you claw back.",
    ],
    finePrint: "*outcomes may vary; this outcome varied all the way",
    trigger: { type: "deathsPending", atLeast: 1 },
    repeatable: true,
    effects: [],
    choices: [
      {
        label: "IGNORE — let it run",
        effects: [{ type: "resolveDeaths" }],
      },
      {
        label: "RESPOND — spend on PR",
        effects: [
          { type: "spendFundingPerDeath", perDeath: METERS.respondCostPerDeath },
          { type: "clawbackRepPerDeath", perDeath: METERS.respondRepPerDeath },
          { type: "clawbackHeatPerDeath", perDeath: METERS.respondHeatPerDeath },
          { type: "resolveDeaths" },
        ],
      },
      {
        label: "SETTLE — pay it away",
        effects: [
          { type: "spendFundingPerDeath", perDeath: METERS.settleCostPerDeath },
          { type: "clawbackRepPerDeath", perDeath: METERS.settleRepPerDeath },
          { type: "clawbackHeatPerDeath", perDeath: METERS.settleHeatPerDeath },
          { type: "advanceCompliance" },
          { type: "resolveDeaths" },
        ],
      },
    ],
  },
  {
    id: "quarterlyOutcomes",
    title: "Quarterly Outcomes Report",
    body: [
      "This quarter's outcomes exceeded projections. Projections were adjusted last quarter to ensure this.",
      "Patient satisfaction remains unmeasured and therefore excellent. Recidivism is now reported as 'repeat engagement,' a growth metric.",
    ],
    finePrint: "*a full methodology is available; it is one sentence long",
    trigger: { type: "everyTicks", period: METERS.quarterlyPeriodTicks },
    repeatable: true,
    effects: [],
  },
  {
    id: "businessProposition",
    title: "A Business Proposition",
    body: [
      "A soft-spoken man has been admiring your patient volume. He represents interested parties with complementary product lines.",
      "The pitch is elegant: you already make the customers. He can make sure they never run out of reasons to come back. Two businesses, one funnel.",
      "Accept, and a second ledger opens — the one you keep in the back office. It pays better. It also attracts a different kind of attention.",
    ],
    finePrint: "*the DEA is a federal agency; this letter is not, and disclaims all knowledge of it",
    trigger: { type: "frontierAtLeast", tier: "residential" },
    effects: [{ type: "unlockCriminal" }],
  },
  {
    id: "deaSurveillance",
    title: "RE: The Van Across The Street",
    body: [
      "You may have noticed a utility van that never does any utility work. It has been there for a while. It is very interested in your back-office ledger.",
      "Nothing to do but keep the officials paid and the paperwork creative. The attention doesn't go away on its own.",
    ],
    finePrint: "*the van is unmarked; the interest is not",
    trigger: { type: "all", of: [{ type: "criminalActive" }, { type: "everyTicks", period: 120 }] },
    repeatable: true,
    effects: [{ type: "deaSpike", amount: 8 }],
  },
  {
    id: "interceptedShipment",
    title: "Shipment Intercepted",
    body: [
      "One of your containers was opened at the port by someone who wasn't paid to look the other way.",
      "The loss is absorbable. The paper trail is the problem — and it leads exactly where paper trails lead.",
    ],
    finePrint: "*container contents listed as \"assorted\"; assortment now federal evidence",
    trigger: { type: "all", of: [{ type: "criminalActive" }, { type: "everyTicks", period: 170 }] },
    repeatable: true,
    effects: [{ type: "deaSpike", amount: 14 }],
  },
];
